import { alpha, createTheme, darken, lighten } from '@mui/material';
import {
  colorBrandPrimary,
  colorBrandPrimaryHover,
  colorBrandPrimaryActive,
  colorBrandPrimarySubtle,
  colorBrandSecondary,
  colorBrandSecondaryHover,
  colorBrandSecondaryActive,
  colorBrandSecondarySubtle,
  colorNeutralEspresso,
  colorNeutralTostado,
  colorNeutralLatte,
  colorNeutralCrema,
  colorNeutralWhite,
  colorTextPrimary,
  colorTextSecondary,
  colorTextDisabled,
  colorTextInverse,
  colorSurfaceDefault,
  colorSurfaceSubtle,
  colorSurfacePage,
  colorSurfaceInverse,
  colorBorderSubtle,
  colorBorderDefault,
  colorBorderStrong,
  colorActionPrimaryText,
  colorActionDisabledBg,
  colorActionDisabledText,
  colorFeedbackSuccess,
  colorFeedbackSuccessSubtle,
  colorFeedbackWarning,
  colorFeedbackWarningSubtle,
  colorFeedbackError,
  colorFeedbackErrorSubtle,
  colorFeedbackInfo,
  colorFeedbackInfoSubtle,
  spaceXs,
  spaceMd,
  spaceLg,
  spaceXl,
  radiusSm,
  radiusMd,
  radiusLg,
  radiusPill,
  shadowSm,
  shadowMd,
  shadowLg,
  shadowBrand,
  breakpointMobile,
  breakpointTablet,
  breakpointDesktop,
  breakpointDesktopLarge,
  fontFamilyBase,
  fontWeightRegular,
  fontWeightMedium,
  fontWeightSemiBold,
  fontWeightBold,
  fontSizeProductH1,
  fontSizeProductH2,
  fontSizeProductH3,
  fontSizeProductH4,
  fontSizeProductH5,
  fontSizeProductH6,
  fontSizeProductBody1,
  fontSizeProductBody2,
  fontSizeProductButton,
  fontSizeProductCaption,
  fontLineHeightTight,
  fontLineHeightSnug,
  fontLineHeightNormal
} from '@mozzo/design-tokens';

const toNumber = (value: string) => Number.parseFloat(value);

const elevation = [
  'none',
  shadowSm,
  shadowSm,
  shadowMd,
  shadowMd,
  shadowMd,
  shadowMd,
  shadowMd,
  shadowLg,
  shadowLg,
  shadowLg,
  shadowLg,
  shadowLg,
  shadowLg,
  shadowLg,
  shadowLg,
  shadowLg,
  shadowLg,
  shadowLg,
  shadowLg,
  shadowLg,
  shadowLg,
  shadowLg,
  shadowLg,
  shadowLg
];

const scale = (base: string) => ({
  lighter: alpha(base, 0.1),
  light: lighten(base, 0.3),
  main: base,
  dark: darken(base, 0.2)
});

const alphaScale = (base: string) => ({
  5: alpha(base, 0.02),
  10: alpha(base, 0.1),
  30: alpha(base, 0.3),
  50: alpha(base, 0.5),
  70: alpha(base, 0.7),
  100: base
});

export const mozzoTheme = createTheme({
  colors: {
    gradients: {
      blue1: `linear-gradient(135deg, ${colorBrandPrimary} 0%, ${colorBrandPrimaryActive} 100%)`,
      blue2: `linear-gradient(135deg, ${colorBrandSecondary} 0%, ${colorBrandPrimary} 100%)`,
      blue3: `linear-gradient(135deg, ${colorNeutralTostado} 0%, ${colorNeutralEspresso} 100%)`,
      orange1: `linear-gradient(135deg, ${colorNeutralCrema} 0%, ${colorBrandSecondary} 100%)`,
      orange2: `linear-gradient(135deg, ${colorBrandSecondary} 0%, ${colorBrandPrimary} 100%)`,
      purple1: `linear-gradient(135deg, ${colorNeutralLatte} 0%, ${colorNeutralTostado} 100%)`,
      pink1: `linear-gradient(135deg, ${colorBrandPrimarySubtle} 0%, ${colorBrandPrimary} 100%)`,
      pink2: `linear-gradient(135deg, ${colorBrandPrimary} 0%, ${colorNeutralEspresso} 100%)`,
      green1: `linear-gradient(135deg, ${colorNeutralCrema} 0%, ${colorBrandSecondary} 100%)`,
      black1: `linear-gradient(135deg, ${colorNeutralTostado} 0%, ${colorNeutralEspresso} 100%)`
    },
    shadows: {
      success: shadowMd,
      error: shadowMd,
      primary: shadowBrand,
      warning: shadowMd
    },
    alpha: {
      white: alphaScale(colorNeutralWhite),
      trueWhite: alphaScale(colorNeutralWhite),
      black: alphaScale(colorNeutralEspresso)
    },
    primary: scale(colorBrandPrimary),
    secondary: scale(colorBrandSecondary),
    success: scale(colorFeedbackSuccess),
    warning: scale(colorFeedbackWarning),
    error: scale(colorFeedbackError),
    info: scale(colorFeedbackInfo)
  },
  general: {
    reactFrameworkColor: colorBrandPrimary,
    borderRadiusSm: radiusSm,
    borderRadius: radiusMd,
    borderRadiusLg: radiusLg,
    borderRadiusXl: '24px'
  },
  sidebar: {
    background: colorSurfaceDefault,
    boxShadow: shadowSm,
    width: '280px',
    textColor: colorTextPrimary,
    dividerBg: colorBorderSubtle,
    menuItemColor: colorTextSecondary,
    menuItemColorActive: colorActionPrimaryText,
    menuItemBg: 'transparent',
    menuItemBgActive: colorBrandPrimary,
    menuItemIconColor: colorNeutralLatte,
    menuItemIconColorActive: colorActionPrimaryText,
    menuItemHeadingColor: colorTextDisabled
  },
  header: {
    height: '64px',
    background: colorSurfaceDefault,
    boxShadow: shadowSm,
    textColor: colorTextPrimary
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: toNumber(breakpointMobile),
      md: toNumber(breakpointTablet),
      lg: toNumber(breakpointDesktop),
      xl: toNumber(breakpointDesktopLarge)
    }
  },
  palette: {
    mode: 'light',
    primary: {
      main: colorBrandPrimary,
      dark: colorBrandPrimaryActive,
      light: colorBrandPrimarySubtle,
      contrastText: colorTextInverse
    },
    secondary: {
      main: colorBrandSecondary,
      dark: colorBrandSecondaryActive,
      light: colorBrandSecondarySubtle,
      contrastText: colorTextInverse
    },
    success: {
      main: colorFeedbackSuccess,
      light: colorFeedbackSuccessSubtle,
      contrastText: colorTextInverse
    },
    warning: {
      main: colorFeedbackWarning,
      light: colorFeedbackWarningSubtle,
      contrastText: colorTextInverse
    },
    error: {
      main: colorFeedbackError,
      light: colorFeedbackErrorSubtle,
      contrastText: colorTextInverse
    },
    info: {
      main: colorFeedbackInfo,
      light: colorFeedbackInfoSubtle,
      contrastText: colorTextInverse
    },
    text: {
      primary: colorTextPrimary,
      secondary: colorTextSecondary,
      disabled: colorTextDisabled
    },
    background: {
      default: colorSurfacePage,
      paper: colorSurfaceDefault
    },
    divider: colorBorderSubtle
  },
  shape: {
    borderRadius: toNumber(radiusSm)
  },
  spacing: toNumber(spaceXs),
  typography: {
    fontFamily: fontFamilyBase,
    fontWeightRegular,
    fontWeightMedium,
    fontWeightBold,
    h1: {
      fontSize: fontSizeProductH1,
      lineHeight: fontLineHeightTight,
      fontWeight: fontWeightBold
    },
    h2: {
      fontSize: fontSizeProductH2,
      lineHeight: fontLineHeightTight,
      fontWeight: fontWeightBold
    },
    h3: {
      fontSize: fontSizeProductH3,
      lineHeight: fontLineHeightSnug,
      fontWeight: fontWeightBold
    },
    h4: {
      fontSize: fontSizeProductH4,
      lineHeight: fontLineHeightSnug,
      fontWeight: fontWeightSemiBold
    },
    h5: {
      fontSize: fontSizeProductH5,
      lineHeight: fontLineHeightSnug,
      fontWeight: fontWeightSemiBold
    },
    h6: {
      fontSize: fontSizeProductH6,
      lineHeight: fontLineHeightSnug,
      fontWeight: fontWeightSemiBold
    },
    subtitle1: {
      fontSize: fontSizeProductBody1,
      lineHeight: fontLineHeightNormal,
      fontWeight: fontWeightMedium,
      color: colorTextSecondary
    },
    subtitle2: {
      fontSize: fontSizeProductBody2,
      lineHeight: fontLineHeightNormal,
      fontWeight: fontWeightMedium,
      color: colorTextSecondary
    },
    body1: {
      fontSize: fontSizeProductBody1,
      lineHeight: fontLineHeightNormal,
      fontWeight: fontWeightRegular
    },
    body2: {
      fontSize: fontSizeProductBody2,
      lineHeight: fontLineHeightNormal,
      fontWeight: fontWeightRegular
    },
    button: {
      fontSize: fontSizeProductButton,
      lineHeight: fontLineHeightTight,
      fontWeight: fontWeightBold,
      textTransform: 'none'
    },
    caption: {
      fontSize: fontSizeProductCaption,
      lineHeight: fontLineHeightSnug,
      fontWeight: fontWeightRegular,
      color: colorTextSecondary
    },
    overline: {
      fontSize: fontSizeProductCaption,
      lineHeight: fontLineHeightSnug,
      fontWeight: fontWeightBold,
      textTransform: 'uppercase',
      letterSpacing: '0.06em'
    }
  },
  shadows: elevation as never,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: colorSurfacePage,
          color: colorTextPrimary,
          fontFamily: fontFamilyBase
        }
      }
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true
      },
      styleOverrides: {
        root: {
          borderRadius: radiusLg,
          paddingLeft: spaceLg,
          paddingRight: spaceLg,
          minHeight: 44
        },
        sizeSmall: {
          minHeight: 36,
          fontSize: fontSizeProductBody2,
          paddingLeft: spaceMd,
          paddingRight: spaceMd
        },
        sizeLarge: {
          minHeight: 52,
          paddingLeft: spaceXl,
          paddingRight: spaceXl
        },
        containedPrimary: {
          backgroundColor: colorBrandPrimary,
          color: colorActionPrimaryText,
          '&:hover': {
            backgroundColor: colorBrandPrimaryHover
          },
          '&:active': {
            backgroundColor: colorBrandPrimaryActive
          },
          '&.Mui-disabled': {
            backgroundColor: colorActionDisabledBg,
            color: colorActionDisabledText
          }
        },
        containedSecondary: {
          backgroundColor: colorBrandSecondary,
          color: colorTextInverse,
          '&:hover': {
            backgroundColor: colorBrandSecondaryHover
          },
          '&:active': {
            backgroundColor: colorBrandSecondaryActive
          }
        },
        outlinedPrimary: {
          borderColor: colorBrandPrimary,
          color: colorBrandPrimary,
          '&:hover': {
            borderColor: colorBrandPrimaryHover,
            backgroundColor: colorBrandPrimarySubtle
          }
        },
        textPrimary: {
          color: colorBrandPrimary,
          '&:hover': {
            backgroundColor: colorBrandPrimarySubtle
          }
        }
      }
    },
    MuiPaper: {
      defaultProps: {
        elevation: 3
      },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          borderRadius: radiusMd
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: radiusMd,
          boxShadow: shadowMd,
          border: `1px solid ${colorBorderSubtle}`
        }
      }
    },
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
        color: 'inherit'
      },
      styleOverrides: {
        root: {
          backgroundColor: colorSurfaceDefault,
          color: colorTextPrimary,
          borderBottom: `1px solid ${colorBorderSubtle}`
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: radiusSm,
          backgroundColor: colorSurfaceDefault,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: colorBorderDefault
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: colorBorderStrong
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: colorBrandPrimary,
            borderWidth: 2
          }
        },
        input: {
          fontSize: fontSizeProductBody1
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: colorTextSecondary,
          '&.Mui-focused': {
            color: colorBrandPrimary
          }
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: radiusPill,
          fontWeight: fontWeightSemiBold
        },
        colorPrimary: {
          backgroundColor: colorBrandPrimarySubtle,
          color: colorBrandPrimaryActive
        },
        colorSecondary: {
          backgroundColor: colorBrandSecondarySubtle,
          color: colorBrandSecondaryActive
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        standardSuccess: {
          backgroundColor: colorFeedbackSuccessSubtle,
          color: colorFeedbackSuccess
        },
        standardWarning: {
          backgroundColor: colorFeedbackWarningSubtle,
          color: colorFeedbackWarning
        },
        standardError: {
          backgroundColor: colorFeedbackErrorSubtle,
          color: colorFeedbackError
        },
        standardInfo: {
          backgroundColor: colorFeedbackInfoSubtle,
          color: colorFeedbackInfo
        }
      }
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: colorSurfaceSubtle
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: colorBorderSubtle,
          fontSize: fontSizeProductBody2
        },
        head: {
          fontWeight: fontWeightBold,
          color: colorTextPrimary
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: radiusMd
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colorSurfaceInverse,
          fontSize: fontSizeProductCaption,
          borderRadius: radiusSm
        }
      }
    }
  }
});

export default mozzoTheme;
